import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Smartphone, Tablet, Monitor, ArrowRight, Check, 
  Star, Heart, Target, Users, Zap, ChevronDown,
  Lightbulb, MessageSquare, TrendingUp, Award,
  Sparkles, RefreshCw, Copy, Share2
} from 'lucide-react';
import { SwipeNavigator, TouchDecisionTree } from './MobileInteractionLibrary';

interface ResponsiveLessonProps {
  title: string;
  content: React.ReactNode;
  interactions: Array<{
    id: string;
    type: 'choice' | 'comparison' | 'builder' | 'decision-tree';
    title: string;
    data: any;
  }>;
  onProgress?: (step: number, data: any) => void;
}

// Responsive lesson container that adapts to device size
export const ResponsiveLessonContainer: React.FC<ResponsiveLessonProps> = ({
  title,
  content,
  interactions,
  onProgress
}) => {
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('mobile');
  const [currentInteraction, setCurrentInteraction] = useState(0);
  const [completedInteractions, setCompletedInteractions] = useState<Set<number>>(new Set());

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) setDeviceType('mobile');
      else if (width < 1024) setDeviceType('tablet');
      else setDeviceType('desktop');
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleInteractionComplete = (data: any) => {
    const newCompleted = new Set(completedInteractions).add(currentInteraction);
    setCompletedInteractions(newCompleted);
    
    if (onProgress) {
      onProgress(currentInteraction, data);
    }

    // Auto-advance to next interaction
    if (currentInteraction < interactions.length - 1) {
      setTimeout(() => {
        setCurrentInteraction(currentInteraction + 1);
      }, 1500);
    }
  };

  const getLayoutClasses = () => {
    switch (deviceType) {
      case 'mobile':
        return 'max-w-lg mx-auto px-4 space-y-4';
      case 'tablet':
        return 'max-w-2xl mx-auto px-6 space-y-6';
      case 'desktop':
        return 'max-w-4xl mx-auto px-8 space-y-8 grid grid-cols-1 lg:grid-cols-2 gap-8';
      default:
        return 'max-w-lg mx-auto px-4 space-y-4';
    }
  };

  return (
    <div className={getLayoutClasses()}>
      {/* Device indicator */}
      <div className="flex justify-center mb-4">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {deviceType === 'mobile' && <Smartphone className="w-4 h-4" />}
          {deviceType === 'tablet' && <Tablet className="w-4 h-4" />}
          {deviceType === 'desktop' && <Monitor className="w-4 h-4" />}
          Optimized for {deviceType}
        </div>
      </div>

      {/* Header */}
      <div className={deviceType === 'desktop' ? 'lg:col-span-2' : ''}>
        <h1 className={`font-bold mb-4 ${
          deviceType === 'mobile' ? 'text-xl' : 
          deviceType === 'tablet' ? 'text-2xl' : 'text-3xl'
        }`}>
          {title}
        </h1>
        
        {/* Progress indicator */}
        <div className="flex justify-center mb-6">
          <div className="flex gap-2">
            {interactions.map((_, index) => (
              <div
                key={index}
                className={`rounded-full transition-all ${
                  index === currentInteraction ? 'bg-blue-500 w-8 h-2' :
                  completedInteractions.has(index) ? 'bg-green-500 w-2 h-2' : 'bg-gray-300 w-2 h-2'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={deviceType === 'desktop' ? 'lg:col-span-1' : ''}>
        <Card>
          <CardContent className={deviceType === 'mobile' ? 'p-4' : 'p-6'}>
            {content}
          </CardContent>
        </Card>
      </div>

      {/* Interactive Section */}
      <div className={deviceType === 'desktop' ? 'lg:col-span-1' : ''}>
        <InteractionRenderer
          interaction={interactions[currentInteraction]}
          deviceType={deviceType}
          onComplete={handleInteractionComplete}
          isCompleted={completedInteractions.has(currentInteraction)}
        />
      </div>

      {/* Summary for completed lesson */}
      {completedInteractions.size === interactions.length && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={deviceType === 'desktop' ? 'lg:col-span-2' : ''}
        >
          <LessonSummary 
            completedCount={completedInteractions.size}
            totalCount={interactions.length}
            deviceType={deviceType}
          />
        </motion.div>
      )}
    </div>
  );
};

// Renders different interaction types based on device
const InteractionRenderer: React.FC<{
  interaction: any;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  onComplete: (data: any) => void;
  isCompleted: boolean;
}> = ({ interaction, deviceType, onComplete, isCompleted }) => {
  if (!interaction) return null;

  switch (interaction.type) {
    case 'choice':
      return (
        <ChoiceInteraction
          {...interaction.data}
          deviceType={deviceType}
          onComplete={onComplete}
          isCompleted={isCompleted}
        />
      );
    case 'comparison':
      return (
        <ComparisonInteraction
          {...interaction.data}
          deviceType={deviceType}
          onComplete={onComplete}
          isCompleted={isCompleted}
        />
      );
    case 'decision-tree':
      return (
        <DecisionTreeInteraction
          {...interaction.data}
          deviceType={deviceType}
          onComplete={onComplete}
          isCompleted={isCompleted}
        />
      );
    case 'builder':
      return (
        <BuilderInteraction
          {...interaction.data}
          deviceType={deviceType}
          onComplete={onComplete}
          isCompleted={isCompleted}
        />
      );
    default:
      return null;
  }
};

// Choice-based interaction component
const ChoiceInteraction: React.FC<{
  title: string;
  question: string;
  options: Array<{ id: string; label: string; description?: string; icon?: React.ReactNode }>;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  onComplete: (data: any) => void;
  isCompleted: boolean;
}> = ({ title, question, options, deviceType, onComplete, isCompleted }) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);

  const handleSelect = (optionId: string) => {
    setSelected(optionId);
    setShowResult(true);
    
    setTimeout(() => {
      onComplete({ selected: optionId, type: 'choice' });
    }, 1000);
  };

  const getGridCols = () => {
    if (deviceType === 'mobile') return 'grid-cols-1';
    if (deviceType === 'tablet') return 'grid-cols-2';
    return 'grid-cols-2 lg:grid-cols-3';
  };

  return (
    <Card className={isCompleted ? 'border-green-200 bg-green-50' : ''}>
      <CardHeader>
        <CardTitle className={`${
          deviceType === 'mobile' ? 'text-lg' : 'text-xl'
        } flex items-center gap-2`}>
          {isCompleted ? (
            <Check className="w-5 h-5 text-green-600" />
          ) : (
            <MessageSquare className="w-5 h-5 text-blue-600" />
          )}
          {title}
        </CardTitle>
        {!isCompleted && (
          <p className={`text-gray-600 ${deviceType === 'mobile' ? 'text-sm' : 'text-base'}`}>
            {question}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {!showResult ? (
          <div className={`grid gap-3 ${getGridCols()}`}>
            {options.map((option, index) => (
              <motion.button
                key={option.id}
                className={`p-4 rounded-lg border text-left transition-all hover:bg-gray-50 ${
                  deviceType === 'mobile' ? 'p-3' : 'p-4'
                }`}
                onClick={() => handleSelect(option.id)}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center gap-3">
                  {option.icon && <div className="text-blue-600">{option.icon}</div>}
                  <div>
                    <div className={`font-medium ${deviceType === 'mobile' ? 'text-sm' : 'text-base'}`}>
                      {option.label}
                    </div>
                    {option.description && (
                      <div className={`text-gray-600 ${deviceType === 'mobile' ? 'text-xs' : 'text-sm'}`}>
                        {option.description}
                      </div>
                    )}
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-green-800 mb-2">Great Choice!</h3>
            <p className="text-green-700 text-sm">
              You selected: {options.find(opt => opt.id === selected)?.label}
            </p>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

// Comparison interaction for comparing outputs
const ComparisonInteraction: React.FC<{
  title: string;
  options: Array<{ id: string; title: string; content: string; rating?: number }>;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  onComplete: (data: any) => void;
  isCompleted: boolean;
}> = ({ title, options, deviceType, onComplete, isCompleted }) => {
  const [selected, setSelected] = useState<string | null>(null);

  const handleSelect = (optionId: string) => {
    setSelected(optionId);
    onComplete({ selected: optionId, type: 'comparison' });
  };

  return (
    <Card className={isCompleted ? 'border-green-200 bg-green-50' : ''}>
      <CardHeader>
        <CardTitle className={`${
          deviceType === 'mobile' ? 'text-lg' : 'text-xl'
        } flex items-center gap-2`}>
          <TrendingUp className="w-5 h-5 text-purple-600" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`space-y-4 ${deviceType === 'desktop' ? 'grid grid-cols-2 gap-4 space-y-0' : ''}`}>
          {options.map((option, index) => (
            <motion.div
              key={option.id}
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                selected === option.id ? 'border-purple-500 bg-purple-50' : 'hover:bg-gray-50'
              }`}
              onClick={() => handleSelect(option.id)}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-medium">{option.title}</h4>
                {option.rating && (
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`w-3 h-3 ${
                          i < option.rating! ? 'text-yellow-400 fill-current' : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
              <p className={`text-gray-700 ${deviceType === 'mobile' ? 'text-sm' : 'text-base'} line-clamp-3`}>
                {option.content}
              </p>
              {selected === option.id && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="mt-3 flex items-center gap-2 text-purple-600"
                >
                  <Check className="w-4 h-4" />
                  <span className="text-sm font-medium">Selected</span>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Decision tree interaction
const DecisionTreeInteraction: React.FC<{
  title: string;
  nodes: Array<{
    id: string;
    question: string;
    options: Array<{ label: string; nextId?: string; result?: any }>;
  }>;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  onComplete: (data: any) => void;
  isCompleted: boolean;
}> = ({ title, nodes, deviceType, onComplete, isCompleted }) => {
  return (
    <Card className={isCompleted ? 'border-green-200 bg-green-50' : ''}>
      <CardHeader>
        <CardTitle className={`${
          deviceType === 'mobile' ? 'text-lg' : 'text-xl'
        } flex items-center gap-2`}>
          <Target className="w-5 h-5 text-blue-600" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <TouchDecisionTree
          nodes={nodes}
          onComplete={(result) => onComplete({ result, type: 'decision-tree' })}
        />
      </CardContent>
    </Card>
  );
};

// Builder interaction (simplified prompt/content builder)
const BuilderInteraction: React.FC<{
  title: string;
  steps: Array<{ id: string; title: string; options: string[] }>;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  onComplete: (data: any) => void;
  isCompleted: boolean;
}> = ({ title, steps, deviceType, onComplete, isCompleted }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState<Record<string, string>>({});

  const handleStepComplete = (stepId: string, selection: string) => {
    const newSelections = { ...selections, [stepId]: selection };
    setSelections(newSelections);

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete({ selections: newSelections, type: 'builder' });
    }
  };

  const currentStepData = steps[currentStep];

  return (
    <Card className={isCompleted ? 'border-green-200 bg-green-50' : ''}>
      <CardHeader>
        <CardTitle className={`${
          deviceType === 'mobile' ? 'text-lg' : 'text-xl'
        } flex items-center gap-2`}>
          <Sparkles className="w-5 h-5 text-purple-600" />
          {title}
        </CardTitle>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index === currentStep ? 'bg-purple-500' :
                  index < currentStep ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          <span className="text-xs text-gray-500">
            Step {currentStep + 1} of {steps.length}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {currentStepData && (
          <div className="space-y-4">
            <h4 className="font-medium">{currentStepData.title}</h4>
            <div className="space-y-2">
              {currentStepData.options.map((option, index) => (
                <motion.button
                  key={index}
                  className="w-full p-3 bg-white border border-gray-200 rounded-lg text-left hover:bg-gray-50 transition-colors"
                  onClick={() => handleStepComplete(currentStepData.id, option)}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <span className={deviceType === 'mobile' ? 'text-sm' : 'text-base'}>
                    {option}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Lesson completion summary
const LessonSummary: React.FC<{
  completedCount: number;
  totalCount: number;
  deviceType: 'mobile' | 'tablet' | 'desktop';
}> = ({ completedCount, totalCount, deviceType }) => {
  return (
    <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
      <CardContent className={deviceType === 'mobile' ? 'p-6' : 'p-8'}>
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <Award className="w-8 h-8 text-white" />
          </motion.div>
          <h3 className={`font-bold text-green-800 mb-2 ${
            deviceType === 'mobile' ? 'text-xl' : 'text-2xl'
          }`}>
            Lesson Complete!
          </h3>
          <p className="text-green-700 mb-4">
            You completed {completedCount} out of {totalCount} interactions
          </p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" className="flex items-center gap-2">
              <Share2 className="w-4 h-4" />
              Share Progress
            </Button>
            <Button className="flex items-center gap-2">
              <ArrowRight className="w-4 h-4" />
              Next Lesson
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ResponsiveLessonContainer;